import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Users, Heart, CheckCircle, ArrowRight, Sparkles, ChevronDown, HelpCircle, Calendar, GraduationCap, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';

// Image URLs for the page
const vendorImages = {
  virginiaBags: 'https://customer-assets.emergentagent.com/job_a036c687-b73f-4aaa-a278-ca6139d7a148/artifacts/2vt2tlx5_Photo%20Dec%2006%202024%2C%2010%2018%2056%20AM.jpg',
  holidayDecor: 'https://customer-assets.emergentagent.com/job_a036c687-b73f-4aaa-a278-ca6139d7a148/artifacts/8i7tj6rs_Photo%20Dec%2006%202024%2C%2010%2020%2020%20AM.jpg',
  softGoods: 'https://customer-assets.emergentagent.com/job_a036c687-b73f-4aaa-a278-ca6139d7a148/artifacts/hvlzj9f5_Photo%20Nov%2009%202024%2C%201%2039%2004%20PM.jpg',
  groupPhoto: 'https://customer-assets.emergentagent.com/job_a036c687-b73f-4aaa-a278-ca6139d7a148/artifacts/2lguwjnn_Photo%20Jul%2030%202025%2C%202%2055%2029%20PM.jpg',
  eventDisplay: 'https://customer-assets.emergentagent.com/job_a036c687-b73f-4aaa-a278-ca6139d7a148/artifacts/5lsr0fcu_IMG_8525.JPG',
};

const benefits = [
  'Access to curated events across the DC Metro area',
  'Booth space at premium venues in Virginia, Maryland & beyond',
  'Marketing and promotional support',
  'Special member pricing for all events and AMS University courses',
  'Access to Monthly Town Hall Meetings',
  'Community networking events',
  'Business mentorship opportunities',
  'Featured in our gallery and social media',
];

const requirements = [
  'Handmade products (no resale items)',
  'Active business license (or in process)',
  'Commitment to quality craftsmanship',
  'Professional presentation standards',
  'Participation in previous years\' events',
  'Maintain good standing with the team',
  'Occasional volunteering at events',
  'Willingness to share your time and knowledge with others',
];

const testimonials = [
  {
    quote: "Joining AMS was the best decision for my jewelry business. The community support and event opportunities have transformed my hobby into a thriving business.",
    author: "Sarah M.",
    role: "Jewelry Designer",
  },
  {
    quote: "The mentorship and education programs helped me understand pricing and marketing. My sales have increased 60% since joining.",
    author: "Marcus T.",
    role: "Woodworker",
  },
  {
    quote: "Being part of this collective means I'm never alone in my business journey. The camaraderie among artisans is invaluable.",
    author: "Elena R.",
    role: "Ceramics Artist",
  },
];

const faqs = [
  {
    question: "How do I become an AMS vendor?",
    answer: "Getting started is easy! Simply fill out our contact form expressing your interest in becoming a vendor. Our team will review your application and reach out to discuss your handmade products and how you can participate in upcoming events. There's no cost to join our community—membership is completely FREE!"
  },
  {
    question: "What types of events does AMS host?",
    answer: "We host a variety of events including artisan markets at wineries and breweries, corporate team-building workshops, community fundraisers, holiday pop-up shops, and our signature Festival of Trees. Each event is carefully curated to provide maximum exposure for our artisans and an exceptional experience for shoppers."
  },
  {
    question: "Do I need to attend events in person?",
    answer: "Not at all! One of the unique benefits of AMS is our consignment model. You can deliver your products to us, and we'll sell them on your behalf at multiple events. This means your products can be in several places at once, maximizing your sales opportunities while you focus on creating more beautiful work."
  },
  {
    question: "What kind of products can I sell?",
    answer: "We welcome all handmade, artisan-crafted products including jewelry, pottery, textiles, woodwork, fine art, home decor, candles, soaps, and more. The key requirement is that items must be handmade—we don't accept mass-produced or resale items. This ensures the quality and uniqueness our customers expect."
  },
  {
    question: "How does pricing and payment work?",
    answer: "We operate on a membership model with options to fit different needs. Members receive special pricing on event participation and AMS University courses. When your items sell, payments are processed through our unified checkout system, and you receive credit for all your sales. Contact us to learn about our current membership options."
  },
  {
    question: "What is AMS University?",
    answer: "AMS University offers educational seminars and workshops designed to help artisans grow their businesses. Topics include product pricing strategies, marketing and social media, booth display tips, customer engagement, and business planning. Members receive special pricing on all courses."
  },
  {
    question: "What makes AMS events different from typical craft shows?",
    answer: "Our events are curated shopping experiences, not traditional booth-based craft fairs. We stage products throughout beautiful venues, creating an inviting atmosphere where shoppers can browse casually. We often include live demonstrations, make-and-take activities, and entertainment. Every item is professionally tagged and tracked through our centralized checkout system."
  }
];

function FAQItem({ faq, isOpen, onToggle, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="border-b border-border last:border-b-0"
    >
      <button
        onClick={onToggle}
        className="w-full py-6 flex items-start justify-between gap-4 text-left hover:text-primary transition-colors"
        data-testid={`faq-question-${index}`}
      >
        <span className="text-lg font-medium text-foreground pr-4">{faq.question}</span>
        <ChevronDown 
          className={`w-5 h-5 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-muted-foreground leading-relaxed whitespace-pre-line" data-testid={`faq-answer-${index}`}>
          {faq.answer}
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function VendorsPage() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="page-transition" data-testid="vendors-page">
      <SEOHead page="vendors" />
      
      {/* Hero Section with Background Image */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${vendorImages.groupPhoto})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl text-white"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              <span>Join Our Community</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-semibold mb-6">
              Become a Vendor
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              Join our award-winning collective of 100+ artisans. Access premium craft show 
              vendor opportunities throughout Washington DC, Virginia, Maryland, West Virginia, 
              Delaware, and Pennsylvania. Let us help you grow your creative business.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg btn-shine" data-testid="vendors-apply-cta">
                Apply to Join
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FREE Membership Highlight Section */}
      <section className="py-16 bg-amber-50 border-y border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center gap-8"
          >
            <div className="flex-shrink-0">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Gift className="w-10 h-10 text-primary" />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-serif font-semibold text-foreground mb-3">
                Membership is <span className="text-primary">FREE</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Joining AMS costs nothing and gives you access to a community of like-minded, highly motivated, 
                and skilled artisans just like you! Connect with fellow makers, share experiences, learn from 
                each other, and grow your business together.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section with Featured Image */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-accent font-accent text-2xl">Membership Benefits</span>
              <h2 className="text-4xl font-serif font-semibold text-foreground mt-2 mb-8">
                What You Get
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={vendorImages.eventDisplay} 
                  alt="AMS Event Display" 
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg border">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Monthly Events</p>
                    <p className="text-sm text-muted-foreground">Year-round opportunities</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Requirements Section with Featured Image */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src={vendorImages.virginiaBags} 
                    alt="Virginia Is For Lovers Bags" 
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg mt-8">
                  <img 
                    src={vendorImages.holidayDecor} 
                    alt="Holiday Decor" 
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg col-span-2">
                  <img 
                    src={vendorImages.softGoods} 
                    alt="Event Display" 
                    className="w-full h-72 object-cover object-center"
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-accent font-accent text-2xl">What We Look For</span>
              <h2 className="text-4xl font-serif font-semibold text-foreground mt-2 mb-8">
                Requirements
              </h2>
              <ul className="space-y-4 mb-8">
                {requirements.map((req, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <Sparkles className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{req}</span>
                  </motion.li>
                ))}
              </ul>
              <div className="bg-white rounded-2xl p-6 shadow-sm border">
                <Heart className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Our Promise</h3>
                <p className="text-muted-foreground">
                  We're committed to helping every member succeed. Our team provides 
                  ongoing support, resources, and opportunities to help you grow your business.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-accent text-2xl">From Our Community</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              Artisans Love AMS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-border rounded-2xl p-8 shadow-sm"
              >
                <p className="text-foreground leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold">{testimonial.author[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{testimonial.author}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="vendors-faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <HelpCircle className="w-4 h-4" />
              <span>You Asked, We Answered</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
              Everything you need to know about becoming an AMS vendor and growing your artisan business with us.
            </p>
          </motion.div>

          <div className="bg-secondary/30 rounded-2xl p-6 md:p-10">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                index={index}
                isOpen={openFAQ === index}
                onToggle={() => toggleFAQ(index)}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <p className="text-muted-foreground mb-4">
              Still have questions? We'd love to hear from you!
            </p>
            <Link to="/contact">
              <Button variant="outline" className="rounded-full" data-testid="faq-contact-btn">
                Contact Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6">
              Ready to Join Our Family?
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Take the first step toward growing your artisan business with the support 
              of our award-winning community. Membership is free—apply today!
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full px-10 py-6 text-lg" data-testid="vendors-contact-cta">
                Contact Us to Apply
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
